import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

// Components
import Button from '../components/Button';
import SectionTitle from '../components/SectionTitle';
import ProductCard from '../components/products/ProductCard';
import HeroSlideshow from '../components/HeroSlideshow';
import PaymentBadges from '../components/PaymentBadges';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroLoading, setHeroLoading] = useState(true);
  
  // Fetch hero slides from Firestore
  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const slidesCollection = collection(db, 'heroSlides');
        const slidesQuery = query(slidesCollection, where("active", "==", true));
        const querySnapshot = await getDocs(slidesQuery);
        
        const slidesList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            image: data.image,
            badge: data.badge,
            title: data.title,
            subtitle: data.subtitle,
            description: data.description,
            overlayType: data.overlayType,
            buttons: [
              { text: data.button1Text, link: data.button1Link, variant: data.button1Variant },
              { text: data.button2Text, link: data.button2Link, variant: data.button2Variant }
            ]
          };
        }).sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // If no slides in Firestore, use default slides
        if (slidesList.length === 0) {
          setHeroSlides([
            {
              image: '/images/hero-gold-coins.jpg',
              badge: 'Premium Collection',
              title: 'Master Collectors Forge',
              subtitle: 'Rare Gold & Silver Treasures',
              description: 'Discover museum-quality coins, precious gems, and investment-grade collectibles from around the world',
              overlayType: 'gold',
              buttons: [
                { text: 'Explore Collection', link: '/categories', variant: 'gold' },
                { text: 'Learn More', link: '/about', variant: 'outline' }
              ]
            }
          ]);
        } else {
          setHeroSlides(slidesList);
        }
      } catch (error) {
        console.error('Error fetching hero slides:', error);
        // Use default slide on error
        setHeroSlides([
          {
            image: '/images/hero-gold-coins.jpg',
            badge: 'Premium Collection',
            title: 'Master Collectors Forge',
            subtitle: 'Rare Gold & Silver Treasures',
            description: 'Discover museum-quality coins, precious gems, and investment-grade collectibles from around the world',
            overlayType: 'gold',
            buttons: [
              { text: 'Explore Collection', link: '/categories', variant: 'gold' },
              { text: 'Learn More', link: '/about', variant: 'outline' }
            ]
          }
        ]);
      } finally {
        setHeroLoading(false);
      }
    };
    
    fetchHeroSlides();
  }, []);

  // Fetch featured products from Firestore
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const productsCollection = collection(db, 'products');
        const featuredQuery = query(productsCollection, where("featured", "==", true));
        const querySnapshot = await getDocs(featuredQuery);
        
        const productsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setFeaturedProducts(productsList);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeaturedProducts();
  }, []);
  
  return (
    <main>
      {/* Hero Slideshow Section */}
      <HeroSlideshow slides={heroSlides} autoPlayInterval={6000} />

      {/* Featured Products Section */}
      <Section $alternate>
        <Container>
          <SectionTitle 
            title="Featured Products" 
            subtitle="Handpicked treasures from our exclusive collection" 
          />
          
          {loading ? (
            <LoadingMessage>Loading featured products...</LoadingMessage>
          ) : featuredProducts.length > 0 ? (
            <ProductGrid>
              {featuredProducts.map(product => (
                <ProductItem key={product.id}>
                  <ProductCard product={product} />
                </ProductItem>
              ))}
            </ProductGrid>
          ) : (
            <EmptyMessage>No featured products available at the moment.</EmptyMessage>
          )}
          
          <CenteredButton>
            <Button to="/categories" variant="primary">View All Products</Button>
          </CenteredButton>
        </Container>
      </Section>

      {/* About Section */}
      <Section>
        <Container>
          <AboutContainer>
            <AboutContent>
              <SectionTitle 
                title="About Master Collectors Forge" 
                centered={false}
              />
              <AboutText>
                <p>
                  Master Collectors Forge is your premier destination for high-quality collectibles. 
                  We specialize in rare coins, commemorative rounds, collectibles, and 
                  exclusive branded merchandise for collectors of all levels.
                </p>
                <p>
                  With decades of experience in the collectibles market, we pride ourselves on 
                  offering authentic, verified items with detailed provenance and competitive pricing.
                </p>
                <p>
                  Our team of experts carefully selects each item in our collection, ensuring that 
                  only the finest pieces make it to our customers. Whether you're a seasoned collector 
                  or just starting your collection journey, we're here to help you find the perfect 
                  additions to your collection.
                </p>
                <Button to="/about" variant="primary">
                  Learn More About Us
                </Button>
              </AboutText>
            </AboutContent>
            <AboutImageContainer>
              <LogoImage src="/images/logo.png" alt="Master Collectors Forge Logo" />
            </AboutImageContainer>
          </AboutContainer>
        </Container>
      </Section>

      {/* Payment Security Section */}
      <Section $alternate>
        <Container>
          <SectionTitle 
            title="Secure & Trusted Payments" 
            subtitle="Shop with confidence - your security is our priority"
          />
          <PaymentBadges />
        </Container>
      </Section>

      {/* Call to Action Section */}
      <CTASection>
        <CTAOverlay />
        <Container>
          <CTAContent>
            <CTATitle>Ready to Start Your Collection?</CTATitle>
            <CTAText>
              Browse our extensive catalog of premium collectibles or contact us for personalized recommendations.
            </CTAText>
            <CTAButtons>
              <Button to="/categories" size="large">
                Shop Now
              </Button>
              <Button to="/contact" variant="outline" size="large">
                Contact Us
              </Button>
            </CTAButtons>
          </CTAContent>
        </Container>
      </CTASection>
    </main>
  );
};

// Styled Components
const Section = styled.section`
  padding: 5rem 0;
  background-color: ${({ $alternate, theme }) => 
    $alternate ? theme.colors.background.light : theme.colors.background.card};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.gradients.gold};
    opacity: 0.3;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const CategoryCard = styled(Link)`
  background: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all ${({ theme }) => theme.transitions.medium};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem;
  text-align: center;
  border: 2px solid transparent;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.gradients.gold};
    transform: scaleX(0);
    transition: transform ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.gold};
    border-color: ${({ theme }) => theme.colors.metallic.gold};
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const CategoryIcon = styled.div`
  font-size: 3.5rem;
  color: ${({ theme }) => theme.colors.metallic.gold};
  margin-bottom: 1.5rem;
  transition: all ${({ theme }) => theme.transitions.medium};
  filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3));
  
  ${CategoryCard}:hover & {
    color: ${({ theme }) => theme.colors.metallic.goldLight};
    transform: scale(1.15) rotate(5deg);
    filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.5));
  }
`;

const CategoryContent = styled.div``;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const CategoryDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const ProductItem = styled.div`
  height: 100%;
`;

const CenteredButton = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const AboutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const AboutContent = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 2;
  }
`;

const AboutText = styled.div`
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    line-height: 1.7;
  }
`;

const AboutImageContainer = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    order: 1;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const CTASection = styled.section`
  background-image: url('/images/cta-background.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  padding: 6rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CTAOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.gradients.premium};
  opacity: 0.95;
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: ${({ theme }) => theme.gradients.gold};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export default HomePage;
